<template>
    <div class="fill-height">
        <template v-if="loading && firstInit">
            <div class="d-flex justify-center align-center fill-height">
                <v-progress-circular
                        indeterminate
                        size="64"
                ></v-progress-circular>
            </div>
        </template>
        <template v-else>
            <div>
                <v-alert
                        v-show="showAlert"
                        dense
                        dismissible
                        text
                        transition="fade-transition"
                        type="info">
                    {{ $t('alerts.displayLastTen') }}
                </v-alert>
                <div class="d-flex">
                    <v-text-field
                            v-model="search"
                            class="datatable-search"
                            dense
                            hide-details
                            outlined
                            prepend-inner-icon="mdi-magnify"/>
                    <div class="mx-2">
                        <DatetimePicker
                                ref="startDate"
                                v-model="startDate"
                                :date-picker-props="{color: 'accent accentText--text', allowedDates: allowedStartDates}"
                                :label="$t('startDate')"
                                :text-field-props="{hideDetails: true, outlined: true, dense: true, appendIcon: startDate ? 'mdi-filter-remove-outline': ''}"
                                :time-picker-props="{color: 'accent accentText--text', format: '24hr', max: maxTime }"
                                event-color="accent"
                                @clear="resetStartDate"
                                @updateDate="updateStartDate">
                            <template v-slot:dateIcon>
                                <v-icon>
                                    mdi-calendar
                                </v-icon>
                            </template>
                            <template v-slot:timeIcon>
                                <v-icon>
                                    mdi-clock
                                </v-icon>
                            </template>
                            <template v-slot:actions="{parent}">
                                <v-btn v-if="parent.activeTab === 0 && showStartToday" color="primary darken-1" text
                                       @click="parent.today">{{ $t('today') }}
                                </v-btn>
                                <template v-if="parent.activeTab === 1">
                                    <v-btn v-if="showStartNow && isStartDateToday" color="primary darken-1" text
                                           @click="parent.now">{{
                                        $t('now')
                                        }}
                                    </v-btn>
                                    <v-btn color="primary darken-1" text @click="parent.okHandler">{{
                                        $t('ok')
                                        }}
                                    </v-btn>
                                </template>
                            </template>
                        </DatetimePicker>
                    </div>
                    <div>
                        <DatetimePicker
                                ref="endDate"
                                v-model="endDate"
                                :date-picker-props="{color: 'accent accentText--text', allowedDates: allowedEndDates}"
                                :label="$t('endDate')"
                                :text-field-props="{hideDetails: true, outlined: true, dense: true, appendIcon: endDate ? 'mdi-filter-remove-outline':''}"
                                :time-picker-props="{color: 'accent accentText--text', format: '24hr', min: minTime}"
                                event-color="secondary"
                                @clear="resetEndDate"
                                @updateDate="updateEndDate">
                            <template v-slot:dateIcon>
                                <v-icon>
                                    mdi-calendar
                                </v-icon>
                            </template>
                            <template v-slot:timeIcon>
                                <v-icon>
                                    mdi-clock
                                </v-icon>
                            </template>
                            <template v-slot:actions="{parent}">
                                <v-btn v-if="parent.activeTab === 0 && showEndToday" color="primary darken-1" text
                                       @click="parent.today">{{ $t('today') }}
                                </v-btn>
                                <template v-if="parent.activeTab === 1">
                                    <v-btn v-if="showEndNow && isEndDateToday" color="primary darken-1" text
                                           @click="parent.now">{{
                                        $t('now')
                                        }}
                                    </v-btn>
                                    <v-btn color="primary darken-1" text @click="parent.okHandler">{{
                                        $t('ok')
                                        }}
                                    </v-btn>
                                </template>
                            </template>
                        </DatetimePicker>
                    </div>
                    <v-btn class="accent ml-2" icon rounded @click="initAlerts(true)">
                        <v-icon color="accentText">mdi-calendar-filter</v-icon>
                    </v-btn>
                </div>
                <v-data-table
                        v-if="items.length"
                        v-model="selectedItems"
                        :headers="header"
                        :items="filteredItems"
                        :loading="loadingTable"
                        :search="search"
                        class="rounded-lg shadow my-6 history-table"
                        :item-class="getRowClass"
                        sort-by="CreatedAt"
                        sort-desc
                        item-key="AlertID"
                        show-select
                        @click:row="showDetails">
                    <template v-slot:[`item.CreatedAt`]="{ item }">
                        {{ formatDateTime(item.CreatedAt) }}
                    </template>
                    <template v-slot:[`item.Severity`]="{ item }">
                        {{ getSeverity(item.Severity) }}
                    </template>
                    <template v-slot:[`item.actions`]="{ item }">
                        <TableAction :tooltip="$t('tooltips.moreDetails')" icon="mdi-information"
                                     @click="showDetails(item)"/>
                    </template>
                    <template v-slot:[`header.Severity`]>
                        <FilterDropDown
                                v-model="filters.severities"
                                :key="filterUpdate + 'severity'"
                                :available-values="Array.from(availableValues.severities)"
                                :filter-values="severities"
                                :name="$t('notifications.severity')"/>
                    </template>
                    <template v-slot:[`header.FullName`]>
                        <FilterDropDown
                                v-model="filters.senders"
                                :key="filterUpdate+'sender'"
                                :filter-values="senders"
                                :name="$t('alerts.sender')"/>
                    </template>
                  <template v-slot:[`footer.prepend`]>
                    <v-btn
                        :disabled="selectedItems.length === 0"
                        :loading="isButtonLoading"
                        class=" py-6 my-4 shadow-inverse accentText--text"
                        color="accent"
                        depressed
                        @click="markAlertsAsRead">
                      {{ $t('notifications.markSelectedAsRead') }}
                    </v-btn>
                  </template>
                </v-data-table>
                <AlertDetails :key="alertID"
                              :user-id="userId || selectedUserId"
                              :alert="alert"
                              :alertID="alertID"
                              :open="openDetails"
                              @close="openDetails = false"/>

            </div>
            <div v-if="!firstInit && loading" class="d-flex justify-center align-center fill-height">
                <v-progress-circular
                        indeterminate
                        size="64"
                ></v-progress-circular>
            </div>
            <div v-if="!items.length && !loading" class="d-flex align-center justify-center fill-height">
                <img :src="require('@/assets/no-geofence.png')" alt="logo">

                <div v-if="$route.name === 'geofenceHistory'" class="font-weight-bold body-1 ml-5">
                    {{ $t('zones.noHistoryOfGeofenceEvents') }}
                </div>
                <div v-else-if="$route.name === 'circleHistory'" class="font-weight-bold body-1 ml-5">
                    {{ $t('circles.noCircleHistory') }}
                </div>
                <div v-else class="font-weight-bold body-1 ml-5">{{ $t('alerts.noAlertHistory') }}</div>
            </div>
        </template>
    </div>
</template>

<script>
import {formatDateTime} from "@/helper/helper";
import {
  NOTIFICATIONS_SEVERITY_CRITICAL,
  NOTIFICATIONS_SEVERITY_INFO,
  NOTIFICATIONS_SEVERITY_WARNING
} from "@/helper/constants";
import AlertDetails from "@/components/Alert/AlertDetails";
import FilterDropDown from "@/components/reusable/FilterDropDown/FilterDropDown";
import DatetimePicker from "@/components/reusable/DatetimePicker/DatetimePicker";

export default {
  name: "AlertHistory",
  components: {FilterDropDown, AlertDetails, DatetimePicker},
  mounted() {
    this.initAlerts();
    window.addEventListener('keyup', this.handleKeyup);
  },
  beforeDestroy() {
    window.removeEventListener('keyup', this.handleKeyup);
  },
  props: {
    userId: {
      type: [Number, null],
      required: true
    },
    filterOpen: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      search: '',
      items: [],
      loading: false,
      loadingTable: false,
      isButtonLoading: false,
      filters: {
        severities: [],
        senders: [],
        senderMails: [],
      },
      availableValues: {
        severities: new Set(),
        senders: new Set(),
        senderMails: new Set(),
      },
      openDetails: false,
      alertID: null,
      alert: {},
      senders: [],
      senderMails: [],
      startDate: null,
      endDate: null,
      startDateOnly: null,
      endDateOnly: null,
      firstInit: true,
      showAlert: false,
      filterUpdate: 0,
      selectedUserId: null,
      selectedItems: [],
    };
  },
  watch: {
    userId() {
      this.initAlerts();
    }
  },
  computed: {
    unreadAlertIDs() {
      return this.$store.getters.getUnreadAlertIDs;
    },
    maxTime() {
      if (!this.endDate || !this.startDateOnly) {
        return '23:59';
      }
      if (this.sameDay(this.endDate, this.startDateOnly)) {
        return this.endDate.getHours() + ':' + this.endDate.getMinutes();
      }
      return '23:59';
    },
    minTime() {
      if (!this.startDate || !this.endDateOnly) {
        return '00:00';
      }
      if (this.sameDay(this.startDate, this.endDateOnly)) {
        return this.startDate.getHours() + ':' + this.startDate.getMinutes();
      }
      return '00:00';
    },
    showStartToday() {
      if (!this.endDate) {
        return true;
      }
      return this.dateToEpoch(this.endDate) - this.dateToEpoch(new Date()) >= 0;
    },
    showEndToday() {
      if (!this.startDate) {
        return true;
      }
      return this.dateToEpoch(new Date()) - this.dateToEpoch(this.startDate) >= 0;
    },
    showStartNow() {
      let maxDate = new Date(),
          maxTime = this.maxTime.split(':');
      maxDate.setHours(maxTime[0]);
      maxDate.setMinutes(maxTime[1]);

      return maxDate.getTime() - new Date().getTime() >= 0;
    },
    showEndNow() {
      let minDate = new Date(),
          minTime = this.minTime.split(':');
      minDate.setHours(minTime[0]);
      minDate.setMinutes(minTime[1]);

      return new Date().getTime() - minDate.getTime() >= 0;
    },
    filteredItems() {
      return this.items.filter(item => this.filters.severities.includes(item.Severity) && this.filters.senders.includes(item.UserID)).map(item => ({
        ...item,
        isRead: this.unreadAlertIDs.includes(item.AlertID)
      }));
    },
    header() {
      return [
        {text: this.$t('date'), value: 'CreatedAt'},
        {text: this.$t('notifications.title'), value: 'Title'},
        {text: this.$t('alerts.sender'), value: 'FullName'},
        {text: this.$t('alerts.senderEmail'), value: 'Email'},
        {text: this.$t('notifications.severity'), value: 'Severity'},
        {text: this.$t('zones.type'), value: 'Type'},
        {text: '', value: 'actions', sortable: false, width: 200},
      ];
    },
    severities() {
      return [
        {
          text: this.$t('notifications.info'),
          value: NOTIFICATIONS_SEVERITY_INFO
        },
        {
          text: this.$t('notifications.warning'),
          value: NOTIFICATIONS_SEVERITY_WARNING
        },
        {
          text: this.$t('notifications.critical'),
          value: NOTIFICATIONS_SEVERITY_CRITICAL
        }
      ];
    },
    isStartDateToday() {
      return this.sameDay(this.startDate, new Date());
    },
    isEndDateToday() {
      return this.sameDay(this.endDate, new Date());
    }
  },
  methods: {
    formatDateTime,
    initAlerts() {
      /*if (filter && !this.startDate && !this.endDate) {
        this.$store.dispatch('addNotification',
            {
              type: 'warning',
              message: this.$t('warnings.needSelectDate')
            });
        return;
      }*/
      let url = '';
      switch (this.$route.name) {
        case 'geofenceHistory':
          url = this.$store.getters.zoneAlerts(this.$route.params.id);
          break;
        case 'circleHistory':
          url = this.$store.getters.circleAlerts(this.$route.params.id);
          break;
        default:
          if(this.userId) {
            url = this.$store.getters.userAlerts(this.userId);
          } else {
            url = this.$store.getters.organizationAlerts;
          }

      }
      this.loading = true;
      this.loadingTable = true;
      let params = {};
      if (this.startDate || this.endDate) {
        params.startDate = this.startDate;
        params.endDate = this.endDate;
      }
      this.axios.get(url, {params}).then(({data}) => {
        this.items = data.Result;

        this.availableValues.severities.clear();
        this.availableValues.senders.clear();
        this.senders = [];
        this.items.forEach(item => {
          this.filters.severities = [];
          this.availableValues.severities.add(item.Severity);
          if (!this.availableValues.senders.has(item.UserID)) {
            this.senders.push({value: item.UserID, text: item.FullName ? item.FullName : ''});
          }
          this.filters.senders = [];
          this.availableValues.senders.add(item.UserID);

        });
        if (this.items.length && this.firstInit) {
          this.showAlert = true;
          setTimeout(() => {
            this.showAlert = false;
          }, 1000 * 10);
        }
        this.firstInit = false;

        this.filterUpdate++;
      }).finally(() => {
        this.loading = false;
        this.loadingTable = false;
      });
    },
    getSeverity(severityID) {
      if (!severityID) {
        return '';
      }
      return this.severities.filter(severity => severity.value === severityID)[0].text;
    },
    showDetails(alert) {
      this.alert = alert;
      this.alertID = alert.AlertID;
      this.selectedUserId = alert.UserID;
      this.openDetails = true;
      if (this.unreadAlertIDs.includes(alert.AlertID)) {
        this.$store.dispatch('setAlertViewed', alert.AlertID)
      }
    },
    allowedStartDates(val) {
      if (!this.endDate) {
        return true;
      }
      return this.dateToEpoch(this.endDate) - this.dateToEpoch(new Date(val)) >= 0;
    },
    dateToEpoch(date) {
      const time = new Date(date);
      time.setHours(0);
      time.setMinutes(0);
      time.setSeconds(0);
      return Math.floor(time.getTime() / 1000);
    },
    allowedEndDates(val) {
      if (!this.startDate) {
        return true;
      }
      return this.dateToEpoch(new Date(val)) - this.dateToEpoch(this.startDate) >= 0;
    },
    updateStartDate(val) {
      this.startDateOnly = new Date(val);
    },
    updateEndDate(val) {
      this.endDateOnly = new Date(val);
    },
    sameDay(d1, d2) {
      return d1.getFullYear() === d2.getFullYear() &&
          d1.getMonth() === d2.getMonth() &&
          d1.getDate() === d2.getDate();
    },
    resetStartDate() {
      this.startDate = null;
    },
    resetEndDate() {
      this.endDate = null;
    },

    handleKeyup(e) {
      console.log(this.openDetails)
      if (e.key === 'Escape' && !this.filterOpen && !this.openDetails) {
        this.$router.back();
      }
    },
    getRowClass(item) {
      if (this.unreadAlertIDs.includes(item.AlertID)) {
        return 'unread';
      }
      return '';
    },
    markAlertsAsRead() {
      const url = this.$store.getters.readMultipleAlerts;
      const ids = this.selectedItems.map(item => item.AlertID)
      this.isButtonLoading = true;

      this.axios.patch(url, ids).then(()=> {
        this.$store.dispatch('getUnreadAlerts');
        this.selectedItems = [];
      }).finally(() =>{
        this.isButtonLoading = false;
      });

    }
  }
};
</script>

<style lang="scss" scoped>
@import "alert-history";
</style>